import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { login } from '../api/client'
import GoogleSignInButton from '../components/shared/GoogleSignInButton'
import { safeReturnTo } from '../utils/returnTo'
import { useLocale } from '../i18n/context'

// Mirrors backend OAUTH_ENABLED (see docs/DECISIONS.md) — flag-disabled
// pending Google credentials + the S-7 server-side state fix.
const OAUTH_ENABLED = import.meta.env.VITE_OAUTH_ENABLED === 'true'

export default function LoginPage() {
  const navigate = useNavigate()
  const { tr } = useLocale()
  const t = tr.loginPage
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // A page that sent the learner here to sign in (a shared text's «Сохранить») is where
  // they return to, through registration too.
  const returnParam = searchParams.get('returnTo')
  const registerHref = returnParam ? `/register?returnTo=${encodeURIComponent(safeReturnTo(returnParam))}` : '/register'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(email, password)
      // Only a path inside this app: anything that could leave the origin is an open redirect.
      navigate(safeReturnTo(searchParams.get('returnTo')), { replace: true })
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: string } } })?.response
      // A refused sign-in is said in the page's language; any other server
      // message is shown as the server sent it.
      const msg = res?.data?.detail
      setError(res?.status !== 401 && typeof msg === 'string' ? msg : t.invalid)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-page)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        backgroundColor: 'var(--bg-surface)',
        borderRadius: 'var(--radius-md)',
        padding: 40,
        boxShadow: 'var(--shadow-float)',
        border: '1px solid var(--border)',
      }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{
            fontWeight: 700, fontSize: 24,
            color: 'var(--text-primary)', letterSpacing: '-0.02em',
            marginBottom: 6,
          }}>
            {'CardSmith' /* i18n-exempt: brand name */}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {t.subtitle}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            backgroundColor: 'var(--danger-soft)',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            color: 'var(--danger)',
            fontSize: 14,
            marginBottom: 20,
          }}>
            {error}
          </div>
        )}

        {/* OAuth — Google */}
        {OAUTH_ENABLED && (
          <>
            <div style={{ marginBottom: 16 }}>
              <GoogleSignInButton onError={(m) => setError(m)} />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                margin: '4px 0 16px',
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
              aria-hidden
            >
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              {t.or}
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              {t.email}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com" // i18n-exempt: an address format, not copy
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              {t.password}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...primaryButtonStyle,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
              marginTop: 4,
            }}
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: 'var(--text-secondary)' }}>
          {t.noAccount}{' '}
          <Link to={registerHref} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
            {t.createOne}
          </Link>
        </p>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  backgroundColor: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '12px 16px',
  color: 'var(--text-primary)',
  fontSize: 15,
  outline: 'none',
  transition: 'border-color 0.15s',
  fontFamily: 'var(--font-ui)',
}

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: 'var(--accent-fill)',
  color: 'var(--text-on-accent)',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '13px 24px',
  fontSize: 15,
  fontWeight: 600,
  width: '100%',
  fontFamily: 'var(--font-ui)',
  transition: 'background-color 0.15s, transform 0.15s',
}
